'use client';
import React from 'react';
import Button from '@pezeshk-book/ui-kit/button';
import Div from '@pezeshk-book/ui-kit/div';
import ListItem from '@pezeshk-book/ui-kit/list-item';
import Text from '@pezeshk-book/ui-kit/text';
import Link from 'next/link';
import CurveContainer from '@elements/curve-container';
import Image from '@elements/image';
import ReactGA from 'react-ga4';
import { useParams, usePathname } from 'next/navigation';
import { useRouter } from '@bprogress/next';
import routes from '@routes';
import { UsersActions } from '@store/users/users-action';
import { useDispatch } from 'react-redux';
import { FooterEtehadieImage, FooterSamandehiImage } from '../../../public/images/footer';
import List from '@pezeshk-book/ui-kit/list';
import AparatIcon from '@icons-components/aparat-icon';
import TelegramIcon from '@icons-components/telegram-icon';
import InstagramIcon from '@icons-components/instagram-icon';
import BackToTopIcon from '@icons-components/back-to-top-icon';

const Footer = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const currentRoute = usePathname();
  const params = useParams<{ slug?: string, id?: string }>();

  const RoutesWithoutFooter: Array<string> = [
    '/adsland',
    '/landingp',
    '/maintenance',
    '/products/checkout',
    '/serviceph',
    `/services/lab-test-home/prescription/order/${params.id}`,
    `/services/online-pharmacy/order/${params.id}`,
    '/services/online-pharmacy/pharmacies-otc',
    '/services/online-pharmacy/pharmacies-otc/checkout',
    '/services/online-pharmacy/pharmacies-upload-prescription',
    '/services/online-pharmacy/pharmacies-upload-prescription/checkout',
    '/services/online-pharmacy/pharmacies-electronic-prescription',
    '/services/online-pharmacy/pharmacies-electronic-prescription/checkout',
    '/services/lab-test-home/checkout',
    '/services/lab-test-home/prescription/checkout',
    '/services/home-care/checkout',
    `/users/${params.slug}/consult`,
    `/financials/order/error/${params.slug}`,
    `/financials/order/${params.id}`,
    `/client-dashboard`,
    `/client-dashboard/orders`,
    `/client-dashboard/orders/pharmacy/${params.id}`,
    `/client-dashboard/orders/laboratory/${params.id}`,
    `/client-dashboard/orders/medical-services/${params.id}`,
    `/client-dashboard/orders/consult/${params.id}`,
    `/client-dashboard/wallet`,
    '/client-dashboard/complaints',
    '/client-dashboard/messages',
    '/client-dashboard/personal-info',
    '/client-dashboard/contact-information',
    '/client-dashboard/comments',
    `/survey/${params.id}`,
  ];
  const dataLinks = [
    {
      slug: 'appointment',
      title: 'پزشکان',
      url: routes['route.users.index'],
      isActive: process.env.APPOINTMENT_SERVICE_ACTIVE === 'true',
    },
    {
      slug: 'pharmacy',
      title: 'داروخانه آنلاین',
      url: routes['route.pharmacies.index'],
      isActive: process.env.PHARMACY_SERVICE_ACTIVE === 'true',
    },
    {
      slug: 'labs',
      title: 'آزمایش در منزل',
      url: routes['route.labs.index'],
      isActive: process.env.LABORATORY_SERVICE_ACTIVE === 'true',
    },
    {
      slug: 'consult',
      title: 'مشاوره آنلاین',
      url: routes['route.users.index'],
      isActive: process.env.CONSULT_SERVICE_ACTIVE === 'true',
      onClick: () => {
        dispatch(UsersActions.setFilterItems({
          key: 'serviceType',
          value: 'مشاوره',
          id: '2',
        }));
      },
    },
    {
      slug: 'posts',
      title: 'مجله سلامت',
      url: routes['route.posts.index'],
      isActive: true,
    },
    {
      slug: 'faq',
      title: 'سوال های متداول',
      url: 'faq',
      isActive: true,
    },
    {
      slug: 'home-care',
      title: 'خدمات پزشکی در منزل',
      url: routes['route.home-care.index'],
      isActive: process.env.HOME_CARE_SERVICE_ACTIVE === 'true',
    },
    {
      slug: 'about-us',
      title: 'درباره ما',
      url: 'about-us',
      isActive: true,
    },
    {
      slug: 'contacts',
      title: 'تماس با ما',
      url: 'contacts',
      isActive: true,
    },
    {
      slug: 'complaints',
      title: 'شکایات',
      url: routes['route.client.orders'],
      isActive: true,
    },
    {
      slug: 'policy',
      title: 'قوانین حریم خصوصی',
      url: 'policy',
      isActive: true,
    },
  ];

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  if (RoutesWithoutFooter.indexOf(currentRoute) !== -1) {
    return null;
  }

  const handleRegisterUser = async () => {
    router.push(`https://dr.${process.env.APP_SITE_URL}`);
    ReactGA.ga('send', 'event', 'physician', 'initiate', 'user try to register');
  };

  return (
    <>
      <CurveContainer gray={true} lastContainer>
        <Div className={'flex-col sm:px-20 w-full'}>
          <Div className={'flex-col w-full justify-center items-center gap-y-8 pt-20 pb-8'}>
            <Div onClick={scrollToTop} className={'absolute -top-[60px] flex self-center z-[4] hover:cursor-pointer active:opacity-70'}>
              <Div className={'relative object-contain w-20 h-20 sm:w-32 sm:h-32'}>
                <BackToTopIcon/>
              </Div>
            </Div>
            <Div className={'w-full items-end gap-1 md:gap-3'}>
              <Div className={'relative object-contain w-6 h-6 md:w-10 md:h-10'}>
                <Image fill={false} width={40} height={40} src={'/images/home-screen/footer-logo.svg'} alt={'logo'}/>
              </Div>
              <Div className={'relative object-contain w-24 h-6 md:w-40 md:h-10'}>
                <Image sizes={'10vw'} src={'/images/home-screen/footer-logo-title.svg'} alt={'logo'}/>
              </Div>
            </Div>
            <Div className={'flex-col gap-y-4 sm:gap-y-6'}>
              <Div className={'w-full'}>
                <Text align={'justify'} className={'w-full justify-between gap-24'}>
                  {process.env.FOOTER_CONTENT}
                </Text>
              </Div>
              <Div className={'flex-col md:flex-row w-full items-center gap-x-20 gap-y-4 sm:gap-y-6'}>
                <Div className={'w-full sm:w-7/12 flex-col gap-y-4 sm:gap-y-6'}>
                  <Div className={'flex-col gap-y-6'}>
                    <Text color={'tertiary'} align={'right'} type={'bold'}>سامانه ثبت نام پزشکان و مراکز</Text>
                    <Div className={'flex-col gap-4'}>
                      <Button
                        size={'large'}
                        onClick={handleRegisterUser}>
                        ثبت نام اولیه پزشکان
                      </Button>
                      <Button
                        size={'large'}
                        onClick={handleRegisterUser}
                        variant={'outlined'}>
                        ورود به حساب پزشکان
                      </Button>
                    </Div>
                  </Div>
                  <List className={'grid grid-cols-3 items-start gap-4'}>
                    {dataLinks.map((item: any, index) => {
                      if (!item.isActive) {
                        return null;
                      }
                      return (
                        <ListItem
                          bulletColor={'secondary'}
                          hasBullet={true}
                          typography={'xs'}
                          key={`footerItem${index}`}
                          className={'w-full items-center !px-0 !py-0'}
                        >
                          <Link
                            className={'whitespace-nowrap text-ellipsis overflow-hidden'}
                            onClick={item?.onClick}
                            href={item?.url.indexOf('/') === 0 ? item?.url : `/${item?.url}`} passHref>
                            {item?.title}
                          </Link>
                        </ListItem>
                      );
                    })}
                  </List>
                </Div>
                <Div className={'flex-col w-full sm:w-2/3 md:w-5/12 gap-y-4 sm:gap-y-6'}>
                  <Div className={'w-full grid grid-cols-3 items-center justify-center md:justify-between gap-4'}>
                    <Link target={'_blank'} referrerPolicy="origin" passHref={true} href='https://ecunion.ir/verify/pezeshkbook.com?token=618411060ace8d9d5082'>
                      <Div className={'relative z-[4] rounded bg-white w-full h-28 sm:h-40 md:h-56'}>
                        <Image sizes={'20vw'} alt={'عضو اتحادیه کشوری کسب و کارهای مجازی'} src={FooterEtehadieImage}/>
                      </Div>
                    </Link>
                    <a referrerPolicy='origin' target='_blank' href='https://trustseal.enamad.ir/?id=116892&Code=illAlZD9lVotcSjzIBOv' className={'relative z-[4] rounded w-full h-28 sm:h-40 md:h-56 flex justify-center items-center bg-white'}>
                      <img className={'object-contain w-[150px] h-[163px]'} referrerPolicy='origin' src='https://trustseal.enamad.ir/logo.aspx?id=116892&Code=illAlZD9lVotcSjzIBOv' alt='نماد اعتماد الکترونیکی' id='illAlZD9lVotcSjzIBOv'/>
                    </a>
                    {/*<Link target={'_blank'} referrerPolicy="origin" passHref={true} href="https://trustseal.enamad.ir/?id=116892&amp;Code=illAlZD9lVotcSjzIBOv">
                      <Div className={'relative z-[4] w-20 h-28 sm:w-32 sm:h-40 md:w-44 md:h-56'}>
                        <Image
                          id="illAlZD9lVotcSjzIBOv"
                          src="https://Trustseal.eNamad.ir/logo.aspx?id=116892&Code=illAlZD9lVotcSjzIBOv"
                          referrerPolicy="origin"
                          alt="نماد اعتماد الکترونیک"
                        />
                      </Div>
                    </Link>*/}
                    <Link target={'_blank'} referrerPolicy="origin" passHref={true} href="https://logo.samandehi.ir/Verify.aspx?id=132924&p=rfthxlaouiwkpfvluiwkaods">
                      <Div className={'relative z-[4] w-full rounded bg-white h-28 sm:h-40 md:h-56'}>
                        <Image sizes={'20vw'} alt={'نشان ملی ثبت (رسانه های دیجیتال)'} src={FooterSamandehiImage}/>
                      </Div>
                    </Link>
                  </Div>

                  <Div className={'flex-col'}>
                    <Text align={'right'} typography={'sm'}>
                      دفتر مرکزی: تهران، ونک، خیابان شیراز جنوبی، خیابان سامان، پلاک 67، واحد 8
                    </Text>
                    <Div className={'w-full gap-1 justify-between items-center mt-2'}>
                      <Text align={'right'}>
                      پشتیبانی پزشک بوک (از ساعت ۹ الی ۲۲):
                      </Text>
                      <a href="tel:+982191004191">
                        <Text>021-91004191</Text>
                      </a>
                    </Div>
                  </Div>
                </Div>
              </Div>
            </Div>
          </Div>
        </Div>
      </CurveContainer>
      <Div className={'flex-col h-20 gap-y-2 bg-primary justify-center items-center'}>
        <Div className={'w-24 h-7 items-center justify-center gap-2 sm:gap-4'}>
          <Link aria-label={'Aparat'} target={'_blank'} href={'https://www.aparat.com/pezeshkbook'} className={'w-6 h-6 relative object-contain'}>
            <AparatIcon/>
          </Link>
          <Link aria-label={'Telegram'} target={'_blank'} href={'https://t.me/joinchat/AAAAAE6cuQzNfNxNhFhFow'} className={'w-6 h-6 relative object-contain'}>
            <TelegramIcon/>
          </Link>
          <Link aria-label={'Instagram'} target={'_blank'} href={'https://www.instagram.com/pezeshkbook/'} className={'w-6 h-6 relative object-contain'}>
            <InstagramIcon/>
          </Link>
        </Div>
        <Text type={'regular'} typography={'sm'} color={'white'}>
            کلیه حقوق مادی و معنوی متعلق به شرکت پردازش موازی آریا است.
        </Text>
      </Div>
    </>
  );
};

export default Footer;
